import React, { FC, useEffect, useState } from 'react';
import { IAutomatedData } from './admin-dictionary';
import { Col, Row, Table, notification } from 'antd';
import Button from '/react4xp/mixins/ui/button/button';
import {
    getAutomatedDictionary,
    synchronizeDictionary,
    synchronizeAllDictionaries
} from './actions';
import { AdminDictionary } from '/react4xp/parts/admin-dictionary/admin-dictionary.type';
import { localize, validatePrivileges } from 'mixins/helpers';

const AutomatedDictionary: FC<AdminDictionary.Props> = (props: AdminDictionary.Props) => {
    const [data, setData] = useState<IAutomatedData[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        onLoadedData();
    }, []);

    const onUpdateAllDictionaries = () => {
        setIsLoading(true);

        synchronizeAllDictionaries()
            .then(({ data }) => {
                const hasSynchronized =
                    Array.isArray(data) &&
                    data.some((item) => item.status === 'SYNCHRONIZED');

                const hasErrors =
                    Array.isArray(data) &&
                    data.some(
                        (item) =>
                            item.status === 'FAILED' ||
                            item.status === 'VALIDATION_FAILED'
                    );

                if (hasSynchronized && !hasErrors) {
                    notification.success({
                        message: 'Справочники успешно обновлены',
                    });
                } else if (hasSynchronized && hasErrors) {
                    notification.warning({
                        message: 'Некоторые справочники обновлены с ошибками',
                    });
                } else {
                    notification.error({
                        message: 'Не удалось обновить справочники',
                    });
                }
            })
            .finally(() => setIsLoading(false));
    };

    const onLoadedData = () => {
        setIsLoading(true);

        getAutomatedDictionary()
            .then(({ data }) => {
                setData(data);
            })
            .catch((error) => {
                console.log(
                    'DICTIONARY ERROR:',
                    error.response?.status,
                    error.response?.data
                );
            })
            .finally(() => setIsLoading(false));
    };

    const onUpdateDictionary = (armDictionaryCode: string) => {
        setIsLoading(true);

        synchronizeDictionary(armDictionaryCode)
            .then(({ data }) => {
                const result = Array.isArray(data)
                    ? data.find(
                        (item) => item.dictionaryCode === armDictionaryCode
                    )
                    : null;

                if (result?.status === 'SYNCHRONIZED') {
                    notification.success({
                        message: 'Справочник успешно обновлен',
                    });
                } else if (
                    result?.status === 'FAILED' ||
                    result?.status === 'VALIDATION_FAILED'
                ) {
                    notification.error({
                        message: 'Не удалось обновить справочник',
                    });
                }
            })
            .finally(() => setIsLoading(false));
    };

    const columns = [
        {
            title: '№',
            render: (_, __, index) => <span>{index + 1}</span>,
        },
        {
            title: localize('dictionary-label', props.translations),
            render: (_, item: IAutomatedData) => {
                const name =
                    props.language === 'kk'
                        ? item.dictionaryNameKk
                        : item.dictionaryNameRu;

                return name?.trim() || item.portalDictionaryCode;
            },
        },
        {
            title: localize('dictionary-code-label', props.translations),
            dataIndex: 'portalDictionaryCode',
        },
        {
            title: '',
            width: 100,
            render: (_, item: IAutomatedData) => (
                <span>
                    {validatePrivileges(props.roles, props.privileges, 'PRIV_DICTIONARY_SYNC') && (
                        <Button
                            className="column-button"
                            style={{ marginRight: 3 }}
                            onClick={() => {
                                onUpdateDictionary(item.armDictionaryCode);
                            }}
                        >
                            Обновить
                        </Button>
                    )}
                </span>
            ),
        },
    ];

    return (
        <div className="poll-screen">
            <Row
                gutter={{ xs: 24, md: 24 }}
                className="my-3"
                justify="space-between"
            >
                <Col></Col>
                <Col>
                    {validatePrivileges(props.roles, props.privileges, 'PRIV_DICTIONARY_SYNC') && (
                        <Button
                            color="gold"
                            onClick={onUpdateAllDictionaries}
                        >
                            Обновить все
                        </Button>
                    )}
                </Col>
            </Row>

            <Table
                className="my-3"
                dataSource={data}
                columns={columns}
                loading={isLoading}
                scroll={{
                    x: true,
                }}
            />
        </div>
    );
};

export default AutomatedDictionary;
